(function(SCS) {
	'use strict';

	var slice = Array.prototype.slice;
	var search = SCS.search = SCS.search || {};
	var util = search.util = search.util || {};

	/**
	 * Creates an instance that processes search requests.
	 * @argument backend The backend function.
	 *           Gets passed the config URL and a callback which it calls with the response data.
	 *           Also gets passed the config object and the processor.
	 *           These should only be used for debugging as the backend may be used to service several requests from different processors and using different configs (as long as the URL stays the same) when behind a deduplicatedBackend or cachingBackend wrapper (or both).
	 *           To respond to config options, use pre- or post-plugins instead.
	 * @argument logger A logger. Should conform to the console interface (methods that may be used are: log, debug, info, error).
	 */
	function SearchProcessor(backend, logger) {
		this.backend = backend || search.backend.defaultBackend();
		this.responders = [];
		this.preFilters = [];
		this.postFilters = [];
		this.logger = logger || window.debug || window.console || null;
		this.running = null;
	}

	var proto = SearchProcessor.prototype;

	proto.responder = function(responder) {
		this.responders.push(responder);
		return responder;
	};

	proto.removeResponder = function(responder) {
		var res = this.responders;
		var index = util.inArray(responder, res);
		if(index > -1) {
			return res.splice(index, 1)[0];
		}
		return null;
	};

	function addToFilterList(filter, list, weight) {
		for(var i=0;i<list.length;i+=1) {
			if(list[i].weight > weight) {
				break;
			}
		}
		list.splice(i, 0, {filter: filter, weight: weight});
	}
	proto.use = function(preFilter, postFilter, weight) {
		weight = weight || 0;
		if(preFilter) {
			addToFilterList(preFilter, this.preFilters, weight);
		}
		if(postFilter) {
			addToFilterList(postFilter, this.postFilters, weight);
		}
	};

	// Syntactic sugar aliases
	proto.usePre = function(filter, weight) { this.use(filter, null, weight); };
	proto.usePost = function(filter, weight) { this.use(null, filter, weight); };

	/**
	* Calls each of the filters in the given filter chain.
	* Expects to be passed the same amount of additional arguments (after context) that:
	* 	1. The filter takes (excluding the callback)
	*		2. The filter passes its callback
	*		3. The done method takes
	* Guarantees the done function be called asynchronously even if none of the filters are asynchronous
	* Each callback (and done) is run in context of the filter chain API, which allows the filter chain to be prematurely aborted.
	*/
	function processAsyncFilterChain(filterChain, done, processor) {
		var args = slice.call(arguments, processAsyncFilterChain.length);
		filterChain = filterChain.slice();

		var running = true;
		var currentFilterAPI = null;

		// Stops the filter chain and ignores the current value (does not call any methods)
		// Called from the outside (the search processor instance)
		function abort() {
			running = false;
			if(currentFilterAPI) {
				currentFilterAPI.abort();
			}
		}
		// Public API for controlling this chain.
		var result = {
			abort: abort,
			processor: processor,
			isRunning: function() {
				return running;
			},
			args: args
		};
		
		// API for controlling this chain from within (calls the processor instance to abort)
		var context = util.extend({}, result, {
			abort: function(silent) {
				abort();
				processor.aborted.apply(processor, [result].concat(args, [silent]));
			}
		});

		var allFiltersSync = true;
		// Called by the filters when they are done processing.
		// Can be synchronously or asynchronously called.
		function nextFilter() {
			// Use the arguments from the last invocation if the filter didn’t pass them.
			for(var i=0;i<args.length;i+=1) {
				args[i] = arguments[i] || args[i];
			}

			if(!running) {
				// Do nothing if already responded or aborted
				// If filters up till now were asynchronous, don’t return a valid controller result;
				result = null;
				return;
			}

			var filter = filterChain.shift();
			if(filter) {
				filter = filter.filter;
			}

			if(!filter && allFiltersSync) {
				// Add a special “async” filter that delays the execution to make filter chains always predictably asynchronous
				filter = function async() {
					var handle = window.setTimeout(function() {
						nextFilter();
					}, 0);
					return {
						abort: function() {
							window.clearTimeout(handle);
						}
					};
				};
			}

			// Filters finished
			if(!filter) {
				// Mark the filter chain as finished (no more callbacks allowed)
				running = false;
				// Clear the API of the current filter
				currentFilterAPI = null;
				// Call the done callback after all filters are done
				done.apply(context, args);

				return;
			}
			// We still have filters to work on, call the next one
			var isSync = false;
			var callback = function() {
				isSync = true;
				return nextFilter.apply(this, arguments);
			};
			// Add the callback as last argument
			currentFilterAPI = filter.apply(context, args.concat([callback]));
			if(!isSync && !running) {
				// Filter aborted before calling a callback
				result = null;
				return;
			}
			if(!isSync && (!currentFilterAPI || !('abort' in currentFilterAPI))) {
				throw new Error('Filter ' + filter.name + ' did not return an API and isn’t synchronous');
			}
		}
		nextFilter();
		// If we got here, something in nextFilter was asynchronous.
		allFiltersSync = false;

		return result;
	}

	proto.process = function(configuration) {
		var _this = this;
		// Called when all filters are done
		function callBackend(configuration) {
			var isSync = false;
			var running = _this.backend.call(_this, configuration.url, function(data) {
				isSync = true;
				_this.running = null;
				_this.processingDone(data, configuration);
			}, _this, configuration);
			 _this.running = _this.running || running; // _this.running may be set in _this.processingDone (for sync backends). Don’t overwrite it!
			if(!isSync && (!_this.running || !('abort' in _this.running))) {
				throw new Error('Backend ' + _this.backend.name + ' did not return an API and isn’t synchronous');
			}
		}

		this.abort();

		this.running = processAsyncFilterChain(this.preFilters, callBackend, this, configuration);
	};

	proto.processingDone = function(data, configuration) {
		var _this = this;
		if(data instanceof Error) {
			return _this.send('error', data, configuration);
		}
		function callResponder(data, configuration) {
			_this.running = null;
			_this.data(data, configuration);
		}
		_this.running = processAsyncFilterChain(this.postFilters, callResponder, this, data, configuration);
	};

	// Aborts the currently active filter queue/backend request
	proto.abort = function() {
		var running = this.running;
		if(running) {
			this.running = null;
			// Abort currently running
			running.abort();
			// Emit the “abort” event
			var args = running.args || [];
			this.aborted(running, args[0], args[1]);
		}
	};
	
	// If this is called, the filter chain was already aborted, either from the outside or the inside
	// Use the “silent” argument when you immediately follow the abort with a new processing phase
	proto.aborted = function aborted(filterChain, data, configuration, silent) {
		if(arguments.length < aborted.length) {
			// No data given (abort was in pre-filter chain)
			silent = configuration;
			configuration = data;
			data = undefined;
		}
		if(filterChain === this.running) {
			this.running = null;
		}
		var eventName = silent ? 'silent-abort' : 'abort';
		this.send(eventName, filterChain, data, configuration);
	};

	// Sends the `data` event with the given data and configuration to all responders
	proto.data = function(data, configuration) {
		this.send('data', data, configuration);
	};

	// Sends an event to all responders
	proto.send = function(event, data) {
		// jshint unused: false
		var args = slice.call(arguments);
		args.splice(1, 0, this);
		// Copy responders so concurrent mutations do not affect the iteration
		var responders = this.responders.slice();
		for(var i=0;i<responders.length;i+=1) {
			// Don’t let an exception in one responder cancel the execution of others
			try {
				this.log('debug', 'sending event', event, 'with data', args);
				responders[i].apply(null, args);
			} catch(e) {
				this.log('error', 'responder', responders[i].displayName || responders[i].name, 'threw', e);
			}
		}
	};

	// Track various events.
	// See https://wiki.swisscom.com/display/PD/Tracking+interface
	proto.track = function(event, data) {
		// jshint unused: false
		var args = slice.call(arguments, 1);

		var trackedBy = [];

		var nav = window.navigator;
		if(util.inArray((nav.mockDoNotTrack || nav.doNotTrack || nav.msDoNotTrack), ['1', 'yes']) > -1) {
			return trackedBy;
		}

		// Find eligible trackers and call them
		var trackers = search.trackers || {};
		for(var name in trackers) {
			// jshint forin: false
			var tracker = trackers[name];
			if(!tracker || !tracker[event] || !tracker.hasOwnProperty(event)) {
				// Event not tracked
				continue;
			}
			try {
				tracker[event].apply(null, args);
				trackedBy.push(name);
			} catch(e) {
				this.log('error', 'Tracker', name, 'threw', e);
			}
		}
		return trackedBy;
	};

	proto.log = function log(level) {
		if(!this.logger) {
			return;
		}
		var messages = slice.call(arguments);
		messages.splice(0, log.length, 'SearchProcessor >');
		level = level || 'log';
		if(!(level in this.logger)) {
			// Prepend level if default, otherwise use real level
			messages.splice(0, 0, level.toUpperCase());
			level = 'log';
		}
		this.logger[level].apply(this.logger, messages);
	};

	search.Processor = SearchProcessor;
	if(window.QUnit) {
		// Visible for testing
		search.internals = search.internals || {};
		search.internals.processAsyncFilterChain = processAsyncFilterChain;
	}

})(window.SCS = window.SCS || {});


//////////////////
// WEBPACK FOOTER
// ./clientlibs/js/etc/designs/sitesearch/clientlibs/logic/processing/js/SearchProcessor.js
// module id = 293
// module chunks = 0